import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Camera } from 'lucide-react';
import { SEO, SchemaOrg } from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '';

export const CelebrityPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/celebrity-jewelry`);
        if (!cancelled) setData(res.data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    window.scrollTo(0, 0);
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]">
        <div className="w-6 h-6 border border-[#111] border-t-transparent animate-spin" />
      </div>
    );
  }

  const articles = data?.articles || [];
  const featured = articles[0];
  const rest = articles.slice(1);

  return (
    <div data-testid="celebrity-page" className="bg-[#FAF9F6]">
      <SEO
        title="Celebrity Jewelry — Red-Carpet & Royal Pieces, Decoded"
        description="Editorial reportage on the jewelry worn by the world's most-photographed women. Taylor Swift's Cartier, Zendaya's Bulgari, Kate Middleton's sapphire — and the exact pre-owned pieces to shop similar."
        url="/celebrity-jewelry"
        image={data?.image}
        type="website"
      />
      <SchemaOrg
        type="breadcrumb"
        data={{
          items: [
            { name: 'Home', url: '/' },
            { name: 'Celebrity Jewelry', url: '/celebrity-jewelry' },
          ],
        }}
      />

      {/* Cinematic Hero */}
      <section className="relative bg-[#111] text-white">
        {data?.image && (
          <div className="absolute inset-0 z-0 opacity-45">
            <img src={data.image} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#111] via-[#111]/75 to-[#111]/40" />
          </div>
        )}

        <div className="relative z-10 container-editorial pt-24 pb-20 lg:pt-32 lg:pb-28">
          <nav className="flex items-center gap-3 text-xs tracking-[0.15em] uppercase text-white/60 mb-12">
            <Link to="/" className="hover:text-[#C69C6D] transition-colors">Home</Link>
            <span>/</span>
            <span className="text-white">Celebrity Jewelry</span>
          </nav>

          <div className="flex items-center gap-3 mb-6">
            <Camera className="w-5 h-5 text-[#C69C6D]" strokeWidth={1.5} />
            <p className="overline text-[#C69C6D]">Red Carpet · Royal · Runway</p>
          </div>
          <h1 className="font-heading font-light text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight mb-8">
            Celebrity <span className="italic">Jewelry</span>
          </h1>
          <p className="font-heading italic text-xl lg:text-2xl text-white/85 max-w-3xl mb-6">
            {data?.tagline || "The pieces that define red carpets and royal vaults."}
          </p>
          <p className="text-white/70 leading-relaxed max-w-2xl text-lg">{data?.description}</p>
          <p className="overline text-white/50 mt-10">{data?.article_count} stories</p>
        </div>
      </section>

      {/* Articles */}
      <section className="section-editorial">
        <div className="container-editorial">
          {articles.length === 0 ? (
            <div className="text-center py-20">
              <Camera className="w-10 h-10 text-[#C69C6D] mx-auto mb-6" strokeWidth={1.2} />
              <p className="overline overline-gold mb-3">In Production</p>
              <h2 className="heading-editorial text-3xl text-[#111] mb-4">
                Stories Going <span className="italic">Live Soon</span>
              </h2>
              <p className="text-[#555] max-w-lg mx-auto mb-8">
                Our editorial desk is writing the celebrity jewelry files now. New features publish daily as the content calendar advances — Taylor Swift's Cartier Love, Zendaya's Bulgari Serpenti, Kate Middleton's sapphire, and more.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-3 mb-10">
                {['Taylor Swift', 'Zendaya', 'Kate Middleton', 'Rihanna', 'Blake Lively', 'Meghan Markle'].map((name) => (
                  <span key={name} className="text-[10px] tracking-[0.2em] uppercase border border-[#E5E0D8] px-3 py-1.5 text-[#555]">
                    {name}
                  </span>
                ))}
              </div>
              <Link to="/blog" className="btn-editorial inline-flex">Browse All Articles</Link>
            </div>
          ) : (
            <>
              {featured && (
                <div className="mb-20 pb-20 border-b border-[#E5E0D8]">
                  <div className="flex items-baseline justify-between mb-12 pb-6 border-b border-[#E5E0D8]">
                    <p className="overline overline-gold">Lead Feature</p>
                    <span className="text-xs text-[#888] tracking-wider">{formatDate(featured.published_at)}</span>
                  </div>

                  <Link
                    to={`/blog/${featured.slug}`}
                    className="group grid grid-cols-1 lg:grid-cols-12 gap-12 items-center"
                    data-testid={`celebrity-featured-${featured.slug}`}
                  >
                    <div className="lg:col-span-7 img-zoom aspect-[4/3] overflow-hidden">
                      <img src={featured.image_url || data?.image} alt={featured.title} className="w-full h-full object-cover" />
                    </div>
                    <div className="lg:col-span-5">
                      <p className="overline mb-4">{featured.category || 'Celebrity'}</p>
                      <h2 className="font-heading text-3xl lg:text-5xl font-light leading-[1.1] text-[#111] mb-6 group-hover:text-[#C69C6D] transition-colors duration-500">
                        {featured.title}
                      </h2>
                      <p className="text-[#555] text-lg leading-relaxed mb-8">{featured.excerpt}</p>
                      <span className="inline-flex items-center gap-3 text-xs tracking-[0.2em] uppercase text-[#111] border-b border-[#111] pb-1">
                        Read Feature
                        <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" strokeWidth={1.5} />
                      </span>
                    </div>
                  </Link>
                </div>
              )}

              {rest.length > 0 && (
                <>
                  <div className="flex items-end justify-between mb-12 pb-6 border-b border-[#E5E0D8]">
                    <div>
                      <p className="overline overline-gold mb-3">The Archive</p>
                      <h2 className="heading-editorial text-4xl lg:text-5xl">All Celebrity Features</h2>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
                    {rest.map((post) => (
                      <Link
                        key={post.slug}
                        to={`/blog/${post.slug}`}
                        className="group block"
                        data-testid={`celebrity-article-${post.slug}`}
                      >
                        <div className="aspect-[4/5] overflow-hidden img-zoom mb-6">
                          <img src={post.image_url || data?.image} alt={post.title} className="w-full h-full object-cover" />
                        </div>
                        <p className="overline mb-3 flex items-center gap-3">
                          <span>{post.category || 'Celebrity'}</span>
                          <span className="w-6 h-px bg-[#C69C6D]" />
                          <span className="text-[#888]">{formatDate(post.published_at)}</span>
                        </p>
                        <h3 className="font-heading text-2xl font-light leading-tight text-[#111] mb-3 group-hover:text-[#C69C6D] transition-colors duration-500">
                          {post.title}
                        </h3>
                        <p className="text-[#555] leading-relaxed line-clamp-2">{post.excerpt}</p>
                      </Link>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default CelebrityPage;
